import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Separator } from './ui/separator';
import AddressModal from './AddressModal';
import { toast } from 'sonner';

function ProfileSection({ user, onUserUpdate }) {
  const [editing, setEditing] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    billing_address: {
      street: user?.billing_address?.street || '',
      city: user?.billing_address?.city || '',
      state: user?.billing_address?.state || '',
      postcode: user?.billing_address?.postcode || '',
      country: user?.billing_address?.country || 'Australia'
    },
    postal_address: {
      street: user?.postal_address?.street || '',
      city: user?.postal_address?.city || '',
      state: user?.postal_address?.state || '',
      postcode: user?.postal_address?.postcode || '',
      country: user?.postal_address?.country || 'Australia'
    }
  });
  const [sameAsPostal, setSameAsPostal] = useState(false);
  const [loading, setLoading] = useState(false);

  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await axios.post('/users/change-password', {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });
      toast.success('Password changed successfully!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setChangingPassword(false);
    } catch (error) {
      console.error('Failed to change password:', error);
      const message = error.response?.data?.detail || 'Failed to change password';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e, section = null) => {
    const { name, value } = e.target;
    if (section) {
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [name]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSameAsPostal = (checked) => {
    setSameAsPostal(checked);
    if (checked) {
      setFormData(prev => ({
        ...prev,
        billing_address: { ...prev.postal_address }
      }));
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const response = await axios.put('/user/profile', formData);
      toast.success('Profile updated successfully!');
      // Update the user state with the new data
      if (response.data.user && onUserUpdate) {
        onUserUpdate(response.data.user);
      }
      setEditing(false);
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold text-gray-900">My Profile</h2>
        {!editing && (
          <Button onClick={() => setEditing(true)}>
            Edit Profile
          </Button>
        )}
      </div>

      <Card className="p-6">
        <div className="space-y-6">
          {/* Personal Information */}
          <div>
            <h3 className="text-xl font-semibold mb-4">Personal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                {editing ? (
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                ) : (
                  <p className="text-gray-900">{user?.name}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                {editing ? (
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                ) : (
                  <p className="text-gray-900">{user?.email}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                {editing ? (
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+61 XXX XXX XXX"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                ) : (
                  <p className="text-gray-900">{user?.phone || 'Not provided'}</p>
                )}
              </div>
            </div>
          </div>

          {/* Save/Cancel buttons for profile editing */}
          {editing && (
            <div className="flex gap-2">
              <Button 
                onClick={handleSave}
                disabled={loading}
                className="bg-custom-orange hover:bg-custom-orange"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button 
                variant="outline"
                onClick={() => {
                  setEditing(false);
                  setFormData({
                    name: user?.name || '',
                    email: user?.email || '',
                    phone: user?.phone || '',
                    billing_address: {
                      street: user?.billing_address?.street || '',
                      city: user?.billing_address?.city || '',
                      state: user?.billing_address?.state || '',
                      postcode: user?.billing_address?.postcode || '',
                      country: user?.billing_address?.country || 'Australia'
                    },
                    postal_address: {
                      street: user?.postal_address?.street || '',
                      city: user?.postal_address?.city || '',
                      state: user?.postal_address?.state || '',
                      postcode: user?.postal_address?.postcode || '',
                      country: user?.postal_address?.country || 'Australia'
                    }
                  });
                }}
              >
                Cancel
              </Button>
            </div>
          )}

          <Separator />

          {/* Password Change */}
          {user?.auth_provider === 'email' && (
            <>
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold">Change Password</h3>
                  {!changingPassword && (
                    <Button 
                      variant="outline"
                      onClick={() => setChangingPassword(true)}
                      size="sm"
                    >
                      Change Password
                    </Button>
                  )}
                </div>
                
                {changingPassword && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
                      <input
                        type="password"
                        value={passwordData.currentPassword}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                      <input
                        type="password"
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Confirm Password</label>
                      <input
                        type="password"
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div className="md:col-span-3 flex gap-2">
                      <Button 
                        onClick={handlePasswordChange}
                        disabled={loading}
                        className="bg-custom-orange hover:bg-custom-orange"
                      >
                        {loading ? 'Changing...' : 'Update Password'}
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => {
                          setChangingPassword(false);
                          setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <Separator />
            </>
          )}

          {/* Delivery Address */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Delivery Address</h3>
              <Button
                variant="outline"
                onClick={() => setShowAddressModal(true)}
                className="text-sm"
              >
                {user?.address_line_1 ? 'Edit Address' : 'Add Address'}
              </Button>
            </div>
            {user?.address_line_1 ? (
              <div className="bg-gray-50 p-4 rounded-md">
                <p className="font-medium text-gray-900">{user.name}</p>
                {user.phone && <p className="text-sm text-gray-600">{user.phone}</p>}
                <p className="text-sm text-gray-600">{user.email}</p>
                <p className="text-sm text-gray-600 mt-2">
                  {user.address_line_1}
                  {user.address_line_2 && `, ${user.address_line_2}`}
                </p>
                <p className="text-sm text-gray-600">
                  {user.city}, {user.state} {user.postal_code}
                </p>
                <p className="text-sm text-gray-600">{user.country || 'AU'}</p>
              </div>
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-md">
                <p className="text-sm text-yellow-800">
                  No delivery address on file. Click "Add Address" to set one up.
                </p>
              </div>
            )}
          </div>

          {/* Address Modal */}
          <AddressModal
            open={showAddressModal}
            onClose={async () => {
              setShowAddressModal(false);
              // Refresh user data after address update
              try {
                const response = await axios.get('/user/profile');
                if (onUserUpdate) {
                  onUserUpdate(response.data);
                }
              } catch (error) {
                console.error('Failed to refresh user profile:', error);
              }
            }}
            onSave={async (address) => {
              // Refresh user data to get the updated profile
              try {
                const response = await axios.get('/user/profile');
                if (onUserUpdate) {
                  onUserUpdate(response.data);
                }
              } catch (error) {
                console.error('Failed to refresh user profile:', error);
              }
            }}
            initialAddress={user?.address_line_1 ? {
              name: user.name || '',
              email: user.email || '',
              phone: user.phone || '',
              address_line_1: user.address_line_1 || '',
              address_line_2: user.address_line_2 || '',
              city: user.city || '',
              state: user.state || '',
              postal_code: user.postal_code || '',
              country: user.country || 'AU'
            } : null}
            title="Delivery Address"
            addressType="shipping"
            saveToProfile={true}
          />
        </div>
      </Card>
    </div>
  );
}

export default ProfileSection;
